/*
 * Copyright (c) 2024. Spin in het Web B.V. Apeldoorn NL.
 * Deze software is géén open source. Het auteursrecht van deze software
 * ligt bij Spin in het Web B.V. en/of Jellybean B.V. en/of de auteur.
 * Gebruik van deze software is onderhevig aan een licentieovereenkomst.
 * www.spininhetweb.nl
 */

import {DateTime} from "luxon";
import {hash} from "../types";
import {InjectionToken} from "@angular/core";

/** Onze injectiontoken voor de config tijdens initialisatie van de app*/
export const LOCAL_CONFIG = new InjectionToken<hash<any> | null>(
    'LOCAL_CONFIG'
);

export class LocalConfig {

  static url = "assets/localconfig.json"; //zonder voorslash want tov index
  static deferred = false; // Nieuwe optie voor uitgesteld laden
  private static deferredResolve?: () => void;
  private static content: hash<any> = {};
  private static whenLoadedPromise: Promise<void>;
  private static _loaded: boolean = false; //zijn we al loaded?
  private static waarschuwOngeladenEenmalig = false;

  /**
   * Tijdens opstarten van de app kan je hiermee even wachten tot de config geladen is
   */
  get whenLoaded(): Promise<void> {
    // Geef altijd de (gedeelde) init-promise terug
    return LocalConfig.init();
  }

  /**
   * synchrone check of de config op dit moment geladen is
   */
  get loaded(): boolean {
    return LocalConfig._loaded;
  }

  private get content() {
    return LocalConfig.content;
  }

  /**
   * Geef een waarde terug volgens een key.subkey.key3 pad. Als niet gevonden: geef een opgegeven standaard terug
   * @param path
   * @param standaard
   */
  public $(path: string, standaard: any = null): any {
    if (!this.loaded) {
      if (!LocalConfig.waarschuwOngeladenEenmalig) {
        LocalConfig.waarschuwOngeladenEenmalig = true;
        console.warn('[LocalConfig] Config is nog niet geladen. Wacht op whenLoaded of gebruik een APP_INITIALIZER. Standaardwaarden worden gebruikt.');
      }
      // Geef de aangeleverde standaard terug zolang de config nog niet klaar is
      return standaard;
    }
    let level = this.content;
    for (let stap of path.split('.')) {
      if (stap in level) {
        level = level[stap];
      } else {
        return standaard;
      }
    }
    return level;
  }

  /**
   * Aangeroepen tijdens de init-app hook: lees de config in. Static zodat deze service ook in shared directives enzo herladen kan worden
   */
  static async init(appconfig?: hash<any>): Promise<void> {
    if (LocalConfig._loaded) {
      LocalConfig.whenLoadedPromise ??= Promise.resolve();
      return LocalConfig.whenLoadedPromise;
    }

    // Als we in deferred mode zitten en er is nog geen config:
    if (LocalConfig.deferred && !appconfig) {
      if (!LocalConfig.whenLoadedPromise) {
        LocalConfig.whenLoadedPromise = new Promise(resolve => {
          LocalConfig.deferredResolve = resolve;
        });
      }
      return LocalConfig.whenLoadedPromise;
    }

    // Als al bezig (en niet in deferred mode met ontbrekende config):
    if (LocalConfig.whenLoadedPromise && !LocalConfig.deferredResolve) {
      return LocalConfig.whenLoadedPromise;
    }

    this.logWelkom();
    //is er een appconfig meegestuurd (vanuit een APP_INITIALIZER of uitgestelde init)
    if (appconfig) {
      LocalConfig.content = appconfig;
      LocalConfig.content['_loaded'] = true;
      LocalConfig._loaded = true;
      if (LocalConfig.deferredResolve) {
        LocalConfig.deferredResolve();
        LocalConfig.deferredResolve = undefined;
      } else {
        LocalConfig.whenLoadedPromise = Promise.resolve();
      }
      return LocalConfig.whenLoadedPromise;
    }

    //we zijn nog niet geladen, dus dit is de variant met het fetchen uit assets
    LocalConfig.whenLoadedPromise = new Promise(async resolve => {
      try {
        const response = await fetch(LocalConfig.url);
        if (!response.ok) {
          // In deferred mode geven we geen foutmelding als de default config niet gevonden wordt
          if (!LocalConfig.deferred) {
            console.error(`Fout bij laden configuratie uit ${LocalConfig.url}.`, response.statusText);
          }
          return;
        }
        LocalConfig.content = await response.json();
        LocalConfig.content['_loaded'] = true;
        LocalConfig._loaded = true;
        resolve();
      } catch (e) {
        if (!LocalConfig.deferred) {
          console.error(`Fout bij laden configuratie uit ${LocalConfig.url}.`);
        }
      }
    });
    return LocalConfig.whenLoadedPromise;
  }

  private static logWelkom() {
    //dit is zo'n basale module binnen sihw-ngx, dat dit de ideale plek is voor copyright
    console.log(`SIHW-NGX library (c) 2024 - ${DateTime.now().year} Jellybean B.V., Apeldoorn, The Netherlands.`);
  }
}
